import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiX, FiArrowLeft, FiArrowRight, FiSearch, FiCornerDownLeft, FiZap,
} from 'react-icons/fi';
import { canAccessRoute } from '../../utils/permissions';
import RobotMascot from './RobotMascot';
import {
  DESTINATIONS, INTENTS, TOUR_STEPS, EXAMPLE_COMMANDS, scoreDestination,
} from './catalog';
import './AssistantBot.scss';

const QUIP_COUNT = 5;
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const MOD_LABEL = IS_MAC ? '⌘' : 'Ctrl';

// Decorative sparkles floating around the robot (light design language).
const SPARKS = [
  { left: '4%', top: '16%', size: 20, delay: '0s' },
  { left: '85%', top: '8%', size: 14, delay: '0.8s' },
  { left: '92%', top: '55%', size: 17, delay: '1.6s' },
  { left: '8%', top: '72%', size: 13, delay: '2.4s' },
  { left: '70%', top: '85%', size: 15, delay: '3.2s' },
];

function AssistantBot({ open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role;
  const effectivePermissions = user?.effectivePermissions || null;

  const [phase, setPhase] = useState('home'); // home | intent | tour | leaving
  const [intent, setIntent] = useState(null);
  const [query, setQuery] = useState('');
  const [tourIdx, setTourIdx] = useState(0);
  const [leaving, setLeaving] = useState(null); // { path, label }
  const [quipIdx, setQuipIdx] = useState(-1);
  const [selIdx, setSelIdx] = useState(0);
  const [greeting, setGreeting] = useState(true); // robot waves hello briefly
  const inputRef = useRef(null);
  const timersRef = useRef([]);

  const later = useCallback((fn, ms) => {
    timersRef.current.push(setTimeout(fn, ms));
  }, []);

  // Reset state every time the overlay opens.
  useEffect(() => {
    if (open) {
      setPhase('home');
      setIntent(null);
      setQuery('');
      setTourIdx(0);
      setLeaving(null);
      setQuipIdx(-1);
      setSelIdx(0);
      setGreeting(true);
      timersRef.current.push(setTimeout(() => setGreeting(false), 1600));
      timersRef.current.push(setTimeout(() => inputRef.current?.focus(), 350));
    }
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [open]);

  // Lock page scroll while Navi owns the screen.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const canAccess = useCallback(
    (path) => canAccessRoute(userRole, path, effectivePermissions),
    [userRole, effectivePermissions]
  );

  const accessibleDests = useMemo(
    () => DESTINATIONS.filter((d) => canAccess(d.path)),
    [canAccess]
  );

  const tourSteps = useMemo(
    () => TOUR_STEPS.filter((s) => canAccess(s.path)),
    [canAccess]
  );

  const homeChips = useMemo(
    () => INTENTS.filter((it) => {
      if (it.direct) return canAccess(it.direct);
      if (it.isTour) return tourSteps.length > 0;
      return accessibleDests.some((d) => d.intents.includes(it.id));
    }),
    [accessibleDests, tourSteps, canAccess]
  );

  const exampleCommands = useMemo(
    () => EXAMPLE_COMMANDS.filter((c) => canAccess(c.path)),
    [canAccess]
  );

  const searching = query.trim().length > 0;

  const searchResults = useMemo(() => {
    if (!searching) return [];
    return accessibleDests
      .map((d) => ({ dest: d, score: scoreDestination(d, query, t) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.dest);
  }, [accessibleDests, searching, query, t]);

  const intentDests = useMemo(() => {
    if (!intent) return [];
    const seen = new Set();
    return accessibleDests
      .filter((d) => d.intents.includes(intent))
      .filter((d) => (seen.has(d.path) ? false : (seen.add(d.path), true)));
  }, [accessibleDests, intent]);

  // ── Actions ─────────────────────────────────────────────────
  const depart = useCallback((path, label) => {
    setQuery('');
    setLeaving({ path, label });
    setPhase('leaving');
    later(() => {
      navigate(path);
      onClose();
    }, 1000);
  }, [navigate, onClose, later]);

  const pickChip = useCallback((chip) => {
    if (chip.direct) {
      depart(chip.direct, t(`assistant.chips.${chip.id}.title`));
    } else if (chip.isTour) {
      setTourIdx(0);
      setPhase('tour');
    } else {
      setIntent(chip.id);
      setPhase('intent');
    }
  }, [depart, t]);

  const goBack = useCallback(() => {
    if (query) { setQuery(''); return; }
    if (phase === 'intent' || phase === 'tour') {
      setIntent(null);
      setPhase('home');
      return;
    }
    onClose();
  }, [query, phase, onClose]);

  const pokeRobot = () => {
    if (phase === 'leaving') return;
    setQuipIdx((i) => (i + 1) % QUIP_COUNT);
    later(() => setQuipIdx(-1), 2600);
  };

  // Visible option list (arrow keys + number shortcuts walk this).
  const visibleOptions = useMemo(() => {
    if (phase === 'leaving') return [];
    if (searching) return searchResults.map((d) => ({ type: 'dest', dest: d }));
    if (phase === 'home') return homeChips.map((c) => ({ type: 'chip', chip: c }));
    if (phase === 'intent') return intentDests.map((d) => ({ type: 'dest', dest: d }));
    return [];
  }, [phase, searching, searchResults, homeChips, intentDests]);

  const pickOption = useCallback((opt) => {
    if (!opt) return;
    if (opt.type === 'chip') pickChip(opt.chip);
    else depart(opt.dest.path, t(`navigationWizard.destinations.${opt.dest.i18nKey}.label`));
  }, [depart, pickChip, t]);

  const optionsKey = `${phase}-${intent}-${searching ? query.trim() : ''}`;

  // Keyboard selection resets whenever the visible list changes.
  useEffect(() => { setSelIdx(0); }, [optionsKey]);

  const pickSelected = useCallback(() => {
    if (!visibleOptions.length) return;
    pickOption(visibleOptions[Math.min(selIdx, visibleOptions.length - 1)]);
  }, [visibleOptions, selIdx, pickOption]);

  // ── Keyboard ────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        goBack();
        return;
      }
      if (phase === 'leaving') return;
      const inInput = e.target === inputRef.current;
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && visibleOptions.length > 0) {
        e.preventDefault();
        setSelIdx((i) => {
          const n = visibleOptions.length;
          return (i + (e.key === 'ArrowDown' ? 1 : n - 1)) % n;
        });
        return;
      }
      // Enter inside the input is handled by the form submit.
      if (e.key === 'Enter' && !inInput) {
        e.preventDefault();
        pickSelected();
        return;
      }
      if (!inInput && /^[1-9]$/.test(e.key)) {
        const opt = visibleOptions[Number(e.key) - 1];
        if (opt) {
          e.preventDefault();
          pickOption(opt);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, phase, visibleOptions, pickOption, pickSelected, goBack]);

  const submitCommand = (e) => {
    e.preventDefault();
    if (phase === 'leaving') return;
    if (searching) pickSelected();
  };

  if (!open) return null;

  // ── Robot mood ──────────────────────────────────────────────
  let mood = 'idle';
  if (quipIdx >= 0 || phase === 'leaving') mood = 'happy';
  else if (greeting && phase === 'home' && !searching) mood = 'wave';
  else if (searching && searchResults.length === 0) mood = 'think';
  else if (phase === 'tour') mood = 'point';

  const tourStep = phase === 'tour' ? tourSteps[tourIdx] : null;

  // ── Heading per state ───────────────────────────────────────
  let title = t('assistant.title');
  let subtitle = t('assistant.subtitle');
  let compactTitle = false;
  if (phase === 'leaving' && leaving) {
    title = t('assistant.leaving', { label: leaving.label });
    subtitle = null;
  } else if (searching) {
    title = searchResults.length > 0
      ? t('assistant.searchResults', { query: query.trim() })
      : t('assistant.searchEmpty');
    subtitle = null;
    compactTitle = true;
  } else if (phase === 'intent' && intent) {
    title = t(`assistant.intentPrompts.${intent}`);
    subtitle = null;
    compactTitle = true;
  } else if (phase === 'tour') {
    title = tourStep ? t(`assistant.tour.steps.${tourStep.id}.text`) : t('assistant.tour.done');
    subtitle = null;
    compactTitle = true;
  }

  return createPortal(
    <div className="navi" role="dialog" aria-modal="true" aria-label={t('assistant.botName', 'Navi')}>
      {/* ── Light backdrop ── */}
      <div className="navi__backdrop" aria-hidden="true" />

      {/* ── Top bar ── */}
      <div className="navi__topbar">
        <div className="navi__topbar-left">
          {/* Icon only — the arrow alone reads as "back"; the label just added
              width to the top bar. Kept as the accessible name and tooltip. */}
          <button
            className="navi__back"
            onClick={goBack}
            aria-label={t('assistant.back')}
            title={t('assistant.back')}
          >
            <FiArrowLeft />
          </button>
          <div className="navi__brand">
            <span className="navi__brand-mark"><FiZap /></span>
            <span className="navi__brand-name">{t('assistant.botName')}</span>
            <em>{t('assistant.tagline')}</em>
          </div>
        </div>
        <button className="navi__close" onClick={onClose} aria-label={t('assistant.close')}>
          <FiX />
        </button>
      </div>

      {/* ── Stage ── */}
      <div className="navi__stage">
        <div className="navi__robot-zone" aria-hidden="false">
          <div className="navi__robot-halo" aria-hidden="true" />
          {SPARKS.map((s, i) => (
            <span
              key={i}
              className="navi__spark"
              aria-hidden="true"
              style={{ left: s.left, top: s.top, fontSize: s.size, animationDelay: s.delay }}
            >
              {i % 2 === 0 ? '✦' : '+'}
            </span>
          ))}
          {quipIdx >= 0 && (
            <div className="navi__quip">{t(`assistant.quips.${quipIdx}`)}</div>
          )}
          <RobotMascot mood={mood} onClick={pokeRobot} />
        </div>

        <div className="navi__panel">
          <h1 className={`navi__title ${compactTitle ? 'navi__title--compact' : ''}`}>{title}</h1>
          {subtitle && <p className="navi__subtitle">{subtitle}</p>}

          {/* Quick-access cards */}
          {!searching && phase === 'home' && (
            <div className="navi__cards" key={optionsKey}>
              {homeChips.map((chip, idx) => {
                const Icon = chip.icon;
                return (
                  <button
                    key={chip.id}
                    className={`navi__card ${chip.isTour ? 'navi__card--wide' : ''} ${idx === selIdx ? 'is-selected' : ''}`}
                    style={{ '--card-accent': chip.accent, '--i': idx }}
                    onClick={() => pickChip(chip)}
                    onMouseEnter={() => setSelIdx(idx)}
                  >
                    <span className="navi__card-icon"><Icon /></span>
                    <span className="navi__card-text">
                      <strong>{t(`assistant.chips.${chip.id}.title`)}</strong>
                      <small>{t(`assistant.chips.${chip.id}.sub`)}</small>
                    </span>
                    <kbd className="navi__card-key">{idx + 1}</kbd>
                  </button>
                );
              })}
            </div>
          )}

          {/* Destination list (intent drill-down or search results) */}
          {(searching || phase === 'intent') && (
            <div className="navi__dests" key={optionsKey}>
              {(searching ? searchResults : intentDests).map((dest, idx) => {
                const Icon = dest.icon;
                return (
                  <button
                    key={`${dest.path}-${dest.i18nKey}`}
                    className={`navi__dest ${idx === selIdx ? 'is-selected' : ''}`}
                    style={{ '--i': idx }}
                    onClick={() => depart(dest.path, t(`navigationWizard.destinations.${dest.i18nKey}.label`))}
                    onMouseEnter={() => setSelIdx(idx)}
                  >
                    <span className="navi__dest-icon"><Icon /></span>
                    <span className="navi__dest-text">
                      <strong>{t(`navigationWizard.destinations.${dest.i18nKey}.label`)}</strong>
                      <small>{t(`navigationWizard.destinations.${dest.i18nKey}.description`)}</small>
                    </span>
                    <FiCornerDownLeft className="navi__dest-enter" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Guided tour */}
          {!searching && phase === 'tour' && tourStep && (
            <div className="navi__tour" key={`tour-${tourIdx}`}>
              <div className="navi__tour-card" style={{ '--card-accent': tourStep.accent }}>
                <span className="navi__tour-icon"><tourStep.icon /></span>
                <div className="navi__tour-text">
                  <strong>{t(`assistant.tour.steps.${tourStep.id}.title`)}</strong>
                  <small>{t('assistant.tour.stepLabel', { current: tourIdx + 1, total: tourSteps.length })}</small>
                </div>
              </div>
              <div className="navi__tour-dots">
                {tourSteps.map((s, i) => (
                  <button
                    key={s.id}
                    className={`navi__tour-dot ${i === tourIdx ? 'is-active' : ''}`}
                    onClick={() => setTourIdx(i)}
                    aria-label={t(`assistant.tour.steps.${s.id}.title`)}
                  />
                ))}
              </div>
              <div className="navi__tour-actions">
                <button
                  className="navi__btn navi__btn--primary"
                  onClick={() => depart(tourStep.path, t(`assistant.tour.steps.${tourStep.id}.title`))}
                >
                  {t('assistant.tour.takeMeThere')} <FiArrowRight />
                </button>
                {tourIdx < tourSteps.length - 1 ? (
                  <button className="navi__btn" onClick={() => setTourIdx((i) => i + 1)}>
                    {t('assistant.tour.next')}
                  </button>
                ) : (
                  <button className="navi__btn" onClick={goBack}>
                    {t('assistant.tour.finish')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* No-match helper */}
          {searching && searchResults.length === 0 && (
            <button className="navi__btn navi__btn--ghost" onClick={() => setQuery('')}>
              <FiArrowLeft /> {t('assistant.startOver')}
            </button>
          )}
        </div>
      </div>

      {/* ── Command bar ── */}
      <div className="navi__console">
        <form className="navi__command" onSubmit={submitCommand}>
          <FiSearch className="navi__command-icon" />
          <input
            ref={inputRef}
            type="text"
            className="navi__command-input"
            placeholder={t('assistant.typePlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={phase === 'leaving'}
          />
          {query && (
            <button
              type="button"
              className="navi__command-clear"
              onClick={() => setQuery('')}
              aria-label={t('assistant.close')}
            >
              <FiX />
            </button>
          )}
          <kbd className="navi__command-kbd">{MOD_LABEL} K</kbd>
          <button type="submit" className="navi__command-go" aria-label={t('assistant.kbdOpen')}>
            <FiArrowRight />
          </button>
        </form>

        {exampleCommands.length > 0 && phase !== 'leaving' && (
          <div className="navi__examples">
            <span className="navi__examples-label">{t('assistant.examplesLabel')}</span>
            {exampleCommands.map((cmd) => (
              <button
                key={cmd.id}
                className="navi__example"
                onClick={() => depart(cmd.path, t(`assistant.examples.${cmd.id}`))}
              >
                {t(`assistant.examples.${cmd.id}`)}
              </button>
            ))}
          </div>
        )}

        <div className="navi__tipbar">
          <span className="navi__tip">
            <span className="navi__tip-bulb" aria-hidden="true">💡</span>
            <strong>{t('assistant.tipLabel')}</strong> {t('assistant.tipText')}
          </span>
          <span className="navi__tip-keys">
            <kbd>↑↓</kbd> {t('assistant.kbdSelect')}
            <kbd>Enter</kbd> {t('assistant.kbdOpen')}
            <kbd>Esc</kbd> {t('assistant.kbdClose')}
            <kbd>{MOD_LABEL} K</kbd> {t('assistant.kbdLaunch')}
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AssistantBot;
